import {
  Box,
  Flex,
  Grid,
  GridItem,
  Icon,
  Spacer,
  Text,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  CreditAgreement,
  Receivable,
  Util,
} from '@finley-technologies/finley-types';

import {
  AssignmentSummaryAccordion,
  AssignmentSummaryAccordionRow,
  DollarWithCircleIcon,
  TopLevelStatRow,
} from './SharedComponents';
import { FiPercent, FiHash } from 'react-icons/fi';
import { CalculationTerminologiesPopover } from '~/core/routes/calculation-terminologies/CalculationTerminologiesPopover';
import {
  borrowingBaseSummaryFormatterMap,
  BorrowingBaseSummaryValueType,
} from '~/packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { colors } from '~/packages/common-frontend/colors';
import { Icon as FAIcon } from '~/packages/common-frontend/components/icons';

type StatHeaderProps = {
  reportingDate: Util.FullDateStr;
  fileVersion: number;
};
const StatHeader = ({ reportingDate, fileVersion }: StatHeaderProps) => (
  <Grid templateColumns="repeat(4, 1fr)" templateRows="repeat(1, 56px)">
    <GridItem colSpan={2} alignSelf="center">
      <Text color="gray">{`Reporting Date: ${commonDate.fullDateStrToStr(
        reportingDate,
        'MMMM dd, yyyy',
      )}`}</Text>
      <Text color="gray">File Version: {fileVersion}</Text>
    </GridItem>
    <GridItem
      colSpan={1}
      borderBottom="1px solid"
      borderColor={colors.gray[400]}
      alignSelf="end"
      p="16px"
    >
      <Text fontWeight="bold">Current Portfolio</Text>
    </GridItem>
    <GridItem
      colSpan={1}
      borderBottom="1px solid"
      borderColor={colors.gray[400]}
      alignSelf="end"
    >
      <Box
        bg={colors.aurora[900]}
        color="white"
        p="16px"
        borderRadius="8px 8px 0 0"
      >
        <Text fontWeight="bold">Proposed Portfolio</Text>
      </Box>
    </GridItem>
  </Grid>
);

type TopLevelStatCalcs = {
  totalExposure: number;
  totalBorrowingBaseAmount: number;
  effectiveReceivableAdvanceRate: number;
  totalProposedAssignedReceivableCount: number;
};

const calculateTopLevelStats = (
  assignmentSummaryPreview: Receivable.ReceivableResponseTapePreviewJobs.AggregatedAssignmentSummaryPreview,
): TopLevelStatCalcs => {
  let totalExposure = 0;
  let totalBorrowingBaseAmount = 0;
  let totalProposedAssignedReceivableCount = 0;
  Object.values(assignmentSummaryPreview.borrowingBaseReportMap).forEach(
    (borrowingBaseReport) => {
      totalExposure += borrowingBaseReport.totalExposure;
      totalBorrowingBaseAmount += borrowingBaseReport.totalBorrowingBaseAmount;
      totalProposedAssignedReceivableCount +=
        borrowingBaseReport.totalProposedAssignedReceivableCount;
    },
  );
  return {
    totalExposure,
    totalBorrowingBaseAmount,
    effectiveReceivableAdvanceRate: commonUtils.divideByOrZero(
      totalBorrowingBaseAmount,
      totalExposure,
    ),
    totalProposedAssignedReceivableCount,
  };
};

export const TopLevelStatBlock = ({
  initialPreview,
  proposedPreview,
  initialTopLevelStatCalcs,
  proposedTopLevelStatCalcs,
  topLevelStatCalcsKey,
  loanProductMapKey,
  parsedCreditAgreements,
  creditFacilitySlugMap,
  statIcon,
  statTitle,
  calcTerminologiesLabel,
  valueType,
}: {
  initialPreview: Receivable.ReceivableResponseTapePreviewJobs.AggregatedAssignmentSummaryPreview;
  proposedPreview: Receivable.ReceivableResponseTapePreviewJobs.AggregatedAssignmentSummaryPreview;
  initialTopLevelStatCalcs: TopLevelStatCalcs;
  proposedTopLevelStatCalcs: TopLevelStatCalcs;
  topLevelStatCalcsKey: keyof TopLevelStatCalcs;
  loanProductMapKey: keyof Receivable.Preview.AssignmentBorrowingBaseReportForPreview;
  parsedCreditAgreements: CreditAgreement.DomainEntity.CreditAgreementFullArtifact[];
  creditFacilitySlugMap: Map<string, string>;
  statIcon: React.ReactElement;
  statTitle: string;
  calcTerminologiesLabel?: string;
  valueType: BorrowingBaseSummaryValueType;
}) => {
  return (
    <AssignmentSummaryAccordion
      accordionButton={({ hasAccordionPanel, isExpanded }) => (
        <Box height="100%" width="100%" pt="8px">
          <TopLevelStatRow
            label={
              <Flex alignItems="center">
                {statIcon}
                <Text fontWeight="semibold" fontSize="larger">
                  {statTitle}
                </Text>
                {calcTerminologiesLabel && (
                  <Box display="inline-block" ml="8px">
                    <CalculationTerminologiesPopover
                      termKeyLabel={calcTerminologiesLabel}
                    />
                  </Box>
                )}
              </Flex>
            }
            initial={
              <Text fontWeight="medium" textAlign="justify" color="black">
                {borrowingBaseSummaryFormatterMap[valueType](
                  initialTopLevelStatCalcs[topLevelStatCalcsKey],
                )}
              </Text>
            }
            proposed={
              <>
                <Text fontWeight="medium">
                  {borrowingBaseSummaryFormatterMap[valueType](
                    proposedTopLevelStatCalcs[topLevelStatCalcsKey],
                  )}
                </Text>
                <Spacer />
                {hasAccordionPanel && (
                  <FAIcon
                    icon={
                      isExpanded ? 'circle-chevron-up' : 'circle-chevron-down'
                    }
                    size="large"
                    color={colors.white}
                  />
                )}
              </>
            }
          />
        </Box>
      )}
      accordionPanel={
        <Box mt="24px">
          {_.map(parsedCreditAgreements, (creditAgreement, index) => {
            const {
              borrowingBaseMechanics: {
                calcAgentMechanics: { loanProductMechanics },
              },
              creditFacilityId,
            } = creditAgreement;
            return (
              <Box
                mt={index > 0 ? '16px' : '0px'}
                key={`credit-facility-exposure-${creditFacilityId}`}
              >
                <AssignmentSummaryAccordion
                  accordionButton={({ hasAccordionPanel, isExpanded }) => (
                    <AssignmentSummaryAccordionRow
                      label={creditFacilitySlugMap.get(creditFacilityId)}
                      initialValue={
                        initialPreview.borrowingBaseReportMap[creditFacilityId][
                          topLevelStatCalcsKey
                        ]
                      }
                      proposedValue={
                        proposedPreview.borrowingBaseReportMap[
                          creditFacilityId
                        ][topLevelStatCalcsKey]
                      }
                      valueType={valueType}
                      hasAccordionPanel={hasAccordionPanel}
                      isExpanded={isExpanded}
                      fullRowBackground
                      background="medium"
                      level={2}
                    />
                  )}
                  accordionPanel={
                    <Box>
                      {_.map(loanProductMechanics, (loanProduct) => {
                        return (
                          <Box
                            mt="8px"
                            key={`total-exposure-${loanProduct.entity.id}`}
                          >
                            <AssignmentSummaryAccordionRow
                              label={loanProduct.entity.name}
                              initialValue={
                                (initialPreview.borrowingBaseReportMap[
                                  creditFacilityId
                                ][loanProductMapKey] as {
                                  [loanProductId: string]: number;
                                })[loanProduct.entity.id]
                              }
                              proposedValue={
                                (proposedPreview.borrowingBaseReportMap[
                                  creditFacilityId
                                ][loanProductMapKey] as {
                                  [loanProductId: string]: number;
                                })[loanProduct.entity.id]
                              }
                              valueType={valueType}
                              background="light"
                              level={3}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  }
                  defaultIsExpanded={parsedCreditAgreements.length === 1}
                />
              </Box>
            );
          })}
        </Box>
      }
      isTopLevel
      defaultIsExpanded
    />
  );
};

type AssignmentSummaryTopLevelStatsProps = {
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  initial: Receivable.ReceivableResponseTapePreviewJobs.AggregatedAssignmentSummaryPreview;
  proposed: Receivable.ReceivableResponseTapePreviewJobs.AggregatedAssignmentSummaryPreview;
  parsedCreditAgreements: CreditAgreement.DomainEntity.CreditAgreementFullArtifact[];
  creditFacilitySlugMap: Map<string, string>;
};
export const AssignmentSummaryTopLevelStats = ({
  initial,
  proposed,
  reportingDate,
  fileVersion,
  parsedCreditAgreements,
  creditFacilitySlugMap,
}: AssignmentSummaryTopLevelStatsProps) => {
  const initialTopLevelStats = calculateTopLevelStats(initial);
  const proposedTopLevelStats = calculateTopLevelStats(proposed);

  return (
    <>
      <StatHeader reportingDate={reportingDate} fileVersion={fileVersion} />
      <TopLevelStatBlock
        initialPreview={initial}
        proposedPreview={proposed}
        initialTopLevelStatCalcs={initialTopLevelStats}
        proposedTopLevelStatCalcs={proposedTopLevelStats}
        calcTerminologiesLabel="Total Receivable Exposure"
        statTitle="Total Exposure"
        statIcon={<DollarWithCircleIcon mr="16px" />}
        creditFacilitySlugMap={creditFacilitySlugMap}
        parsedCreditAgreements={parsedCreditAgreements}
        loanProductMapKey="totalExposureByLoanProductId"
        topLevelStatCalcsKey="totalExposure"
        valueType="currency"
      />
      <TopLevelStatBlock
        initialPreview={initial}
        proposedPreview={proposed}
        initialTopLevelStatCalcs={initialTopLevelStats}
        proposedTopLevelStatCalcs={proposedTopLevelStats}
        calcTerminologiesLabel="Total Receivable Borrowing Base Amount"
        statTitle="Total Borrowing Base from Receivables"
        statIcon={<DollarWithCircleIcon mr="16px" />}
        creditFacilitySlugMap={creditFacilitySlugMap}
        parsedCreditAgreements={parsedCreditAgreements}
        loanProductMapKey="totalBorrowingBaseAmountByLoanProductId"
        topLevelStatCalcsKey="totalBorrowingBaseAmount"
        valueType="currency"
      />
      <TopLevelStatBlock
        initialPreview={initial}
        proposedPreview={proposed}
        initialTopLevelStatCalcs={initialTopLevelStats}
        proposedTopLevelStatCalcs={proposedTopLevelStats}
        calcTerminologiesLabel="Effective Receivable Advance Rate"
        statTitle="Receivable Effective Advance Rate"
        statIcon={
          <Icon fontSize="22px" alignSelf="center" mr="16px">
            <FiPercent />
          </Icon>
        }
        creditFacilitySlugMap={creditFacilitySlugMap}
        parsedCreditAgreements={parsedCreditAgreements}
        loanProductMapKey="effectiveReceivableAdvanceRateByLoanProductId"
        topLevelStatCalcsKey="effectiveReceivableAdvanceRate"
        valueType="percentage"
      />
      <TopLevelStatBlock
        initialPreview={initial}
        proposedPreview={proposed}
        initialTopLevelStatCalcs={initialTopLevelStats}
        proposedTopLevelStatCalcs={proposedTopLevelStats}
        statTitle="Total Assigned Receivable Count"
        statIcon={
          <Icon fontSize="22px" alignSelf="center" mr="16px">
            <FiHash />
          </Icon>
        }
        creditFacilitySlugMap={creditFacilitySlugMap}
        parsedCreditAgreements={parsedCreditAgreements}
        loanProductMapKey="proposedAssignedReceivableCountByLoanProductId"
        topLevelStatCalcsKey="totalProposedAssignedReceivableCount"
        valueType="integer"
      />
    </>
  );
};
